import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AtLimit, CharCounter } from '../components/ui/char-counter';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '../components/ui/input-otp';
import { PhoneInput } from '../components/ui/phone-input';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Building2,
  Phone,
  Mail,
  MapPin,
  User,
  Receipt,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import LoadingScreen from '../components/ui/LoadingScreen';
import { showSuccess } from '../components/Alerts';
import { handleLoadError, handleCreateError, handleUpdateError, handleDeleteError } from '../utils/errorHandler';
import { formatCuit, formatPhone } from '../utils/format';
import { supplierService } from '../services/supplierService';
import { Supplier } from '../types';
import { PERMISSIONS } from '../constants/permissions';
import RequirePermission from '../components/RequirePermission';

const ITEMS_PER_PAGE = 12;

const SUPPLIER_CATEGORIES = [
  'Materiales de Construcción',
  'Herramientas y Ferretería',
  'Pinturas y Revestimientos',
  'Madera y Carpintería',
  'Materiales Eléctricos',
  'Plomería y Sanitarios',
  'Servicios',
  'Otros',
];



const Suppliers: React.FC = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  // Selection states
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null);
  const [supplierToView, setSupplierToView] = useState<Supplier | null>(null);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    cuit: '',
    businessName: '',
    category: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    phone: '',
    email: '',
    paymentTerms: '',
    notes: '',
  });

  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await supplierService.getAllSuppliers();
      setSuppliers(data);
    } catch (error) {
      handleLoadError(error, 'proveedores');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  useEffect(() => { setPage(1); }, [searchTerm, categoryFilter, sortBy]);

  const resetForm = () => {
    setFormData({
      cuit: '',
      businessName: '',
      category: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      address: '',
      phone: '',
      email: '',
      paymentTerms: '',
      notes: '',
    });
    setEditingSupplier(null);
    setValidationErrors([]);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const isValidCuit = (cuit: string): boolean => {
    const numbers = cuit.replace(/\D/g, '');
    return numbers.length === 11;
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];
    if (!formData.cuit.trim()) {
      errors.push('El CUIT es obligatorio');
    } else if (!isValidCuit(formData.cuit)) {
      errors.push('El CUIT debe tener 11 dígitos');
    }
    if (!formData.businessName.trim()) {
      errors.push('La razón social es obligatoria');
    }
    if (!formData.category.trim()) {
      errors.push('La categoría es obligatoria');
    }
    return errors;
  };

  const handleAddNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setValidationErrors([]);
    setFormData({
      cuit: supplier.cuit,
      businessName: supplier.businessName,
      category: supplier.category,
      contactName: supplier.contactName,
      contactPhone: supplier.contactPhone,
      contactEmail: supplier.contactEmail || '',
      address: supplier.address,
      phone: supplier.phone,
      email: supplier.email || '',
      paymentTerms: supplier.paymentTerms,
      notes: supplier.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      if (editingSupplier) {
        await supplierService.updateSupplier(editingSupplier.id, formData);
        showSuccess('Proveedor actualizado exitosamente');
      } else {
        await supplierService.createSupplier(formData);
        showSuccess('Proveedor creado exitosamente');
      }
      setIsDialogOpen(false);
      resetForm();
      loadSuppliers();
    } catch (error: any) {
      if (editingSupplier) {
        handleUpdateError(error, 'proveedor');
      } else {
        handleCreateError(error, 'proveedor');
      }
      const errorMessage = error?.response?.data?.message || error?.message;
      if (errorMessage) {
        setValidationErrors([errorMessage]);
      }
    }
  };

  const handleDelete = (supplier: Supplier) => {
    setSupplierToDelete(supplier);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;
    try {
      await supplierService.deleteSupplier(supplierToDelete.id);
      showSuccess('Proveedor eliminado exitosamente');
      loadSuppliers();
    } catch (error) {
      handleDeleteError(error, 'proveedor');
    } finally {
      setIsDeleteDialogOpen(false);
      setSupplierToDelete(null);
    }
  };

  const handleViewDetails = (supplier: Supplier) => {
    setSupplierToView(supplier);
    setIsDetailsDialogOpen(true);
  };

  const getSearchableText = (supplier: Supplier): string => {
    return `${supplier.businessName} ${supplier.category} ${supplier.cuit} ${supplier.contactName} ${supplier.email || ''}`.toLowerCase();
  };

  const filteredSuppliers = suppliers
    .filter(supplier => {
      const matchesSearch = getSearchableText(supplier).includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || supplier.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.businessName.localeCompare(b.businessName);
      if (sortBy === 'category') return a.category.localeCompare(b.category);
      if (sortBy === 'createdAt') return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      return 0;
    });

  const totalPages = Math.ceil(filteredSuppliers.length / ITEMS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageSuppliers = filteredSuppliers.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const categories = Array.from(new Set(suppliers.map(s => s.category)));

  if (loading) {
    return <LoadingScreen message="Cargando proveedores..." />;
  }

  return (
    <div className="w-full h-full p-6 space-y-6">
      {/* Card Container */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border">
          <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 shrink-0">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Proveedores</h1>
                <p className="text-sm text-muted-foreground">
                  Gestiona y visualiza información de todos los proveedores
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <RequirePermission permission={PERMISSIONS.COMPRAS.PROVEEDORES_EDITAR}>
                <Button
                  size="sm"
                  onClick={handleAddNew}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Nuevo Proveedor
                </Button>
              </RequirePermission>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col items-end gap-4 md:flex-row">
            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Categoría
                </label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[200px] rounded-lg">
                    <SelectValue placeholder="Todas las categorías" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Ordenar por
                </label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Razón Social</SelectItem>
                    <SelectItem value="category">Categoría</SelectItem>
                    <SelectItem value="createdAt">Fecha de Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Search */}
            <div className="w-full space-y-2 md:w-72 md:ml-auto">
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar proveedores..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="p-6">
          {filteredSuppliers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-muted/50">
                <Building2 className="w-8 h-8 text-muted-foreground/40" />
              </div>
              <p className="text-base font-medium text-muted-foreground">No hay proveedores</p>
              <p className="mt-1 text-sm text-muted-foreground/70">
                {searchTerm || categoryFilter !== 'all'
                  ? 'Intenta ajustar los filtros de búsqueda'
                  : 'Comienza creando el primer proveedor'}
              </p>
              {!searchTerm && categoryFilter === 'all' && (
                <RequirePermission permission={PERMISSIONS.COMPRAS.PROVEEDORES_EDITAR}>
                  <Button onClick={handleAddNew} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Proveedor
                  </Button>
                </RequirePermission>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {pageSuppliers.map((supplier) => (
                  <div
                    key={supplier.id}
                    className="flex flex-col overflow-hidden transition-all border bg-card border-border rounded-xl hover:shadow-md hover:border-primary/20"
                  >
                    {/* Card Header */}
                    <div className="flex items-start justify-between px-4 pt-4 pb-3">
                      <div className="flex items-center min-w-0 gap-3">
                        <div className="flex-shrink-0 p-2.5 rounded-xl bg-primary/10 text-primary">
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold leading-tight truncate text-foreground">
                            {supplier.businessName}
                          </p>
                          <span className="inline-flex items-center px-2 py-0.5 mt-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
                            {supplier.category}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="mx-4 border-t border-border" />

                    {/* Info rows */}
                    <div className="px-4 py-3 space-y-2">
                      {/* CUIT */}
                      <div className="flex items-center gap-2">
                        <span className="flex-shrink-0 w-10 text-xs font-medium text-muted-foreground">CUIT</span>
                        <span className="font-mono text-xs font-semibold tracking-wide text-foreground">
                          {formatCuit(supplier.cuit)}
                        </span>
                      </div>

                      {/* Email o teléfono */}
                      {(supplier.email || supplier.phone) && (
                        <div className="flex items-center gap-2">
                          {supplier.email ? (
                            <>
                              <Mail className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="text-xs truncate text-foreground">{supplier.email}</span>
                            </>
                          ) : (
                            <>
                              <Phone className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="text-xs text-foreground">{formatPhone(supplier.phone)}</span>
                            </>
                          )}
                        </div>
                      )}

                      {/* Dirección */}
                      {supplier.address && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-foreground line-clamp-1">{supplier.address}</span>
                        </div>
                      )}

                      {/* Condiciones de pago */}
                      {supplier.paymentTerms && (
                        <div className="flex items-center gap-2">
                          <span className="flex-shrink-0 text-xs font-medium text-muted-foreground">Pago</span>
                          <span className="text-xs text-foreground">{supplier.paymentTerms}</span>
                        </div>
                      )}
                    </div>

                    {/* Contact block */}
                    {(supplier.contactName || supplier.contactPhone || supplier.contactEmail) && (
                      <>
                        <div className="mx-4 border-t border-border" />
                        <div className="px-4 py-2.5 bg-muted/30">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Contacto</p>
                          <div className="space-y-1">
                            {supplier.contactName && (
                              <div className="flex items-center gap-2">
                                <User className="flex-shrink-0 w-3 h-3 text-muted-foreground" />
                                <span className="text-xs text-foreground">{supplier.contactName}</span>
                              </div>
                            )}
                            {supplier.contactPhone && (
                              <div className="flex items-center gap-2">
                                <Phone className="flex-shrink-0 w-3 h-3 text-muted-foreground" />
                                <span className="text-xs text-foreground">{supplier.contactPhone}</span>
                              </div>
                            )}
                            {supplier.contactEmail && (
                              <div className="flex items-center gap-2">
                                <Mail className="flex-shrink-0 w-3 h-3 text-muted-foreground" />
                                <span className="text-xs truncate text-foreground">{supplier.contactEmail}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Footer: fecha + acciones */}
                    <div className="mx-4 mt-auto border-t border-border" />
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-muted-foreground">
                        {supplier.createdAt
                          ? new Date(supplier.createdAt).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'
                        }
                      </span>
                      <div className="flex items-center gap-0.5">
<button
                          onClick={() => handleViewDetails(supplier)}
                          className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground"
                          title="Ver detalles"
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                        <RequirePermission permission={PERMISSIONS.COMPRAS.PROVEEDORES_EDITAR}>
                          <button
                            onClick={() => handleEdit(supplier)}
                            className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground"
                            title="Editar proveedor"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(supplier)}
                            className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors text-destructive hover:bg-destructive/10 hover:text-destructive"
                            title="Eliminar proveedor"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </RequirePermission>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Mostrando{' '}
                  <span className="font-semibold text-foreground">{pageSuppliers.length}</span> de{' '}
                  <span className="font-semibold text-foreground">{filteredSuppliers.length}</span>{' '}
                  proveedor{filteredSuppliers.length !== 1 ? 'es' : ''}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          resetForm();
          setValidationErrors([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl font-bold text-foreground">
              {editingSupplier ? <Edit className="w-5 h-5 text-primary" /> : <Plus className="w-5 h-5 text-primary" />}
              {editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Complete la información a continuación
            </DialogDescription>
          </DialogHeader>

          {validationErrors.length > 0 && (
            <div className="p-3 text-sm border rounded-lg bg-destructive/10 border-destructive/20 text-destructive">
              <ul className="list-disc list-inside">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-6 py-4">
            <div className="space-y-4">
              {/* Información fiscal */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="businessName" className="text-xs font-medium text-foreground sm:text-sm">
                    Razón Social <span className="text-xs text-red-500">(Campo obligatorio)</span>
                  </Label>
                  <Input
                    id="businessName"
                    value={formData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    maxLength={255}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                    placeholder="Distribuidora de Materiales SA"
                  />
                  <AtLimit value={formData.businessName} max={255} />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="cuit" className="text-xs font-medium text-foreground sm:text-sm">
                    CUIT <span className="text-xs text-red-500">(Campo obligatorio)</span>
                  </Label>
                  <InputOTP
                    maxLength={11}
                    value={formData.cuit.replace(/\D/g, '')}
                    onChange={(value) => {
                      let formatted = value;
                      if (value.length > 2 && value.length <= 10) {
                        formatted = `${value.slice(0, 2)}-${value.slice(2)}`;
                      } else if (value.length > 10) {
                        formatted = `${value.slice(0, 2)}-${value.slice(2, 10)}-${value.slice(10)}`;
                      }
                      handleInputChange('cuit', formatted);
                    }}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                      <InputOTPSlot index={6} />
                      <InputOTPSlot index={7} />
                      <InputOTPSlot index={8} />
                      <InputOTPSlot index={9} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={10} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category" className="text-xs font-medium text-foreground sm:text-sm">
                    Categoría <span className="text-xs text-red-500">(Campo obligatorio)</span>
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleInputChange('category', value)}
                  >
                    <SelectTrigger id="category" className="rounded-lg bg-background border-border">
                      <SelectValue placeholder="Seleccione categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPLIER_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentTerms" className="text-xs font-medium text-foreground sm:text-sm">
                    Condiciones de Pago
                  </Label>
                  <Input
                    id="paymentTerms"
                    value={formData.paymentTerms}
                    onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                    maxLength={255}
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                    placeholder="Ej: 30 días"
                  />
                  <AtLimit value={formData.paymentTerms} max={255} />
                </div>
              </div>

              {/* Información de contacto */}
              <div className="pt-4 border-t border-border">
                <h4 className="mb-4 text-sm font-semibold text-foreground">Información de Contacto</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs font-medium text-foreground sm:text-sm">Teléfono</Label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(value) => handleInputChange('phone', value)}
                      placeholder="11 1234-5678"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-medium text-foreground sm:text-sm">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      maxLength={255}
                      className="rounded-lg bg-background border-border focus-visible:ring-primary"
                      placeholder="ventas@empresa.com"
                    />
                    <AtLimit value={formData.email} max={255} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address" className="text-xs font-medium text-foreground sm:text-sm">Dirección</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      maxLength={255}
                      className="rounded-lg bg-background border-border focus-visible:ring-primary"
                      placeholder="Calle 123, Ciudad"
                    />
                    <AtLimit value={formData.address} max={255} />
                  </div>
                </div>
              </div>

              {/* Persona de contacto */}
              <div className="pt-4 border-t border-border">
                <h4 className="mb-4 text-sm font-semibold text-foreground">Persona de Contacto</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="contactName" className="text-xs font-medium text-foreground sm:text-sm">Nombre</Label>
                    <Input
                      id="contactName"
                      value={formData.contactName}
                      onChange={(e) => handleInputChange('contactName', e.target.value)}
                      maxLength={255}
                      className="rounded-lg bg-background border-border focus-visible:ring-primary"
                      placeholder="Juan Pérez"
                    />
                    <AtLimit value={formData.contactName} max={255} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactPhone" className="text-xs font-medium text-foreground sm:text-sm">Teléfono Contacto</Label>
                    <PhoneInput
                      value={formData.contactPhone}
                      onChange={(value) => handleInputChange('contactPhone', value)}
                      placeholder="11 1234-5678"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="contactEmail" className="text-xs font-medium text-foreground sm:text-sm">Email Contacto</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      maxLength={255}
                      className="rounded-lg bg-background border-border focus-visible:ring-primary"
                      placeholder="contacto@empresa.com"
                    />
                    <AtLimit value={formData.contactEmail} max={255} />
                  </div>
                </div>
              </div>

              {/* Notas */}
              <div className="pt-4 border-t border-border">
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-xs font-medium text-foreground sm:text-sm">Notas</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    maxLength={255}
                    size="sm"
                    placeholder="Notas adicionales sobre el proveedor..."
                  />
                  <CharCounter value={formData.notes} max={255} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-input hover:bg-accent text-foreground">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-primary text-primary-foreground hover:opacity-90">
              {editingSupplier ? 'Actualizar' : 'Guardar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="rounded-lg bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Confirmar Eliminación
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar a{' '}
              <span className="font-medium text-foreground">{supplierToDelete?.businessName}</span>?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="rounded-lg">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete} className="rounded-lg bg-destructive text-destructive-foreground hover:opacity-90">
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Detalles del Proveedor
            </DialogTitle>
            <DialogDescription>
              Información completa del proveedor
            </DialogDescription>
          </DialogHeader>

          {supplierToView && (
            <div className="pt-4 space-y-4">
              {supplierToView.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Dirección</p>
                    <p className="text-sm text-foreground">{supplierToView.address}</p>
                  </div>
                </div>
              )}

              {supplierToView.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Teléfono</p>
                    <p className="text-sm text-foreground">{supplierToView.phone}</p>
                  </div>
                </div>
              )}

              {supplierToView.email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Email</p>
                    <p className="text-sm text-foreground">{supplierToView.email}</p>
                  </div>
                </div>
              )}

              {supplierToView.paymentTerms && (
                <div className="flex items-start gap-3">
                  <Receipt className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Condiciones de Pago</p>
                    <p className="text-sm text-foreground">{supplierToView.paymentTerms}</p>
                  </div>
                </div>
              )}

              {supplierToView.notes && (
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Notas</p>
                    <p className="mt-1 text-sm text-foreground">{supplierToView.notes}</p>
                  </div>
                </div>
              )}

              {!supplierToView.address &&
                !supplierToView.phone &&
                !supplierToView.email &&
                !supplierToView.paymentTerms &&
                !supplierToView.notes && (
                  <div className="py-6 text-center">
                    <p className="text-sm text-muted-foreground">No hay información adicional disponible</p>
                  </div>
                )}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setIsDetailsDialogOpen(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
